<template>
    <div class="toolbar d-flex justify-content-center">
        <b-button-toolbar
            key-nav
            aria-label="Toolbar with button groups"
            class="d-flex flex-column flex-md-row align-items-center p-2"
        >
            <div>
                <b-button-group class="mx-1">
                    <b-button
                        v-if="$_showSnapButton"
                        size="md"
                        variant="link"
                        :pressed.sync="useSnap"
                        :title="$t('drawing.snap')"
                        v-ng-usage:click="{
                            name: 'feedbackToolbar',
                            button: 'snap'
                        }"
                    >
                        <img
                            src="~@/assets/imported-icons/ic_fluent_sub_grid_24_regular.svg"
                            :alt="$t('drawing.snap')"
                        />
                    </b-button>
                    <b-button
                        size="md"
                        variant="link"
                        @click="resetDrawing(true)"
                        :title="$t('drawing.reset')"
                        v-ng-usage:click="{
                            name: 'feedbackToolbar',
                            button: 'reset'
                        }"
                    >
                        <img
                            src="~@/assets/imported-icons/ic_fluent_arrow_reset_24_regular.svg"
                            :alt="$t('drawing.reset')"
                        />
                    </b-button>
                </b-button-group>
                <b-button-group
                    class="mx-1"
                    v-if="!isWebFeedback && drawObject.mode === DRAW_MODUS.Draw"
                >
                    <b-button
                        size="md"
                        variant="link"
                        @click="useLocationAsPoint"
                        :title="$t('drawing.addCurrentLocation')"
                        v-ng-usage:click="{
                            name: 'feedbackToolbar',
                            button: 'location'
                        }"
                    >
                        <img
                            src="~@/assets/imported-icons/ic_fluent_my_location_24_regular.svg"
                            :alt="$t('drawing.addCurrentLocation')"
                        />
                    </b-button>
                    <b-button
                        size="md"
                        :disabled="drawObject.points <= 1"
                        variant="link"
                        @click="draw.removeLastpoint()"
                        :title="$t('drawing.undo')"
                        v-ng-usage:click="{
                            name: 'feedbackToolbar',
                            button: 'undo'
                        }"
                    >
                        <img
                            src="~@/assets/imported-icons/ic_fluent_arrow_undo_24_regular.svg"
                            :alt="$t('drawing.undo')"
                        />
                    </b-button>
                </b-button-group>
                <b-button-group
                    class="mx-1"
                    v-if="drawObject.mode === DRAW_MODUS.Modify"
                >
                    <b-button
                        size="md"
                        variant="link"
                        :disabled="
                            isUsingPoint ||
                            (isUsingPolygon && drawObject.points < 4) ||
                            (isUsingLine && drawObject.points < 2)
                        "
                        :pressed.sync="useDelete"
                        :title="$t('drawing.delete')"
                        v-ng-usage:click="{
                            name: 'feedbackToolbar',
                            button: 'deleteMode'
                        }"
                    >
                        <img
                            src="~@/assets/imported-icons/ic_fluent_eraser_24_regular.svg"
                            :alt="$t('drawing.delete')"
                        />
                    </b-button>
                </b-button-group>
            </div>
            <div class="mt-2 mt-md-0">
                <b-button-group class="mx-1">
                    <b-button
                        size="md"
                        variant="link"
                        :disabled="!canBePoint"
                        :pressed="isUsingPoint"
                        @click="drawType = FEEDBACKS_GEOMETRY_TYPE_CODES.POINT"
                        :title="$t('drawing.point')"
                        v-ng-usage:click="{
                            name: 'feedbackToolbar',
                            button: 'usePoint'
                        }"
                    >
                        <img
                            src="~@/assets/imported-icons/ic_fluent_location_24_regular.svg"
                            :alt="$t('drawing.point')"
                        />
                    </b-button>
                    <b-button
                        size="md"
                        variant="link"
                        :disabled="!canBeLine"
                        :pressed="isUsingLine"
                        @click="drawType = FEEDBACKS_GEOMETRY_TYPE_CODES.LINE"
                        :title="$t('drawing.line')"
                        v-ng-usage:click="{
                            name: 'feedbackToolbar',
                            button: 'useLine'
                        }"
                    >
                        <img
                            src="~@/assets/imported-icons/ic_fluent_data_line_24_regular.svg"
                            :alt="$t('drawing.line')"
                        />
                    </b-button>
                    <b-button
                        size="md"
                        variant="link"
                        :disabled="!canBePolygon"
                        :pressed="isUsingPolygon"
                        @click="
                            drawType = FEEDBACKS_GEOMETRY_TYPE_CODES.POLYGON
                        "
                        :title="$t('drawing.polygon')"
                        v-ng-usage:click="{
                            name: 'feedbackToolbar',
                            button: 'usePolygon'
                        }"
                    >
                        <img
                            src="~@/assets/icons/ng_fluent_polygon_20_regular.svg"
                            :alt="$t('drawing.polygon')"
                        />
                    </b-button>
                </b-button-group>
            </div>
        </b-button-toolbar>
    </div>
</template>

<script>
    import { mapActions, mapGetters, mapMutations } from 'vuex';
    import { getArea as OlGetArea, getLength as OlGetLength } from 'ol/sphere';

    import {
        CONTENT_TYPE_ID,
        FEEDBACKS_GEOMETRY_TYPE_CODES,
        FEEDBACKS_GEOMETRY_TYPES,
        DRAW_MODUS,
        INTERFACE_PANEL_TRIGGERS
    } from '../../../constants';
    import { DrawOnMap } from '../../../services/map/interaction/draw';

    // localy used constant for drawtype
    const drawObject = {
        points: 0,
        coordinates: null,
        polygon: null,
        area: null,
        length: null,
        mode: null,
        geometryType: null
    };

    export default {
        props: {
            contentTypeId: { type: Number, default: null },
            mapName: { type: Object, required: false },
            geometryType: { type: String, default: 'PL' },
            feature: { type: Object, required: false },
            geometryTypeChoice: { type: String, required: false }
        },

        data() {
            return {
                draw: null,
                useSnap: false,
                useDelete: false,
                contentTypeConst: CONTENT_TYPE_ID,
                FEEDBACKS_GEOMETRY_TYPE_CODES: FEEDBACKS_GEOMETRY_TYPE_CODES,
                DRAW_MODUS: DRAW_MODUS,
                drawType: null,
                drawObject: { ...drawObject }
            };
        },

        computed: {
            ...mapGetters(['getMapByName', 'getMapViewPort', 'getWebTheme']),
            map() {
                return this.getMapByName(this.mapName)?.map;
            },
            viewport() {
                return this.getMapViewPort(this.mapName);
            },
            canBePolygon() {
                return this.geometryType.includes(
                    FEEDBACKS_GEOMETRY_TYPE_CODES.POLYGON
                );
            },
            canBeLine() {
                return this.geometryType.includes(
                    FEEDBACKS_GEOMETRY_TYPE_CODES.LINE
                );
            },
            canBePoint() {
                return this.geometryType.includes(
                    FEEDBACKS_GEOMETRY_TYPE_CODES.POINT
                );
            },
            isUsingPolygon() {
                return (
                    this.currentDrawTypeCode ===
                    FEEDBACKS_GEOMETRY_TYPE_CODES.POLYGON
                );
            },
            isUsingLine() {
                return (
                    this.currentDrawTypeCode === FEEDBACKS_GEOMETRY_TYPE_CODES.LINE
                );
            },
            isUsingPoint() {
                return (
                    this.currentDrawTypeCode === FEEDBACKS_GEOMETRY_TYPE_CODES.POINT
                );
            },
            // returns coordinates to be used when editing existing feature
            loadCoordinates() {
                if (!this.feature) {
                    return null;
                }
                const coordinates = this.feature.getGeometry().getCoordinates();
                if (this.isUsingPoint) {
                    return [coordinates];
                }
                if (this.isUsingPolygon) {
                    const lastTwoElements = coordinates[0].slice(-2);
                    if (
                        JSON.stringify(lastTwoElements[0]) ===
                        JSON.stringify(lastTwoElements[1])
                    ) {
                        return coordinates[0].slice(0, -2);
                    }
                    return coordinates[0].slice(0, -1);
                }
                return coordinates;
            },
            // returns the type in the format of PLV (POINT, LINE, POLYGON)
            currentDrawTypeCode() {
                if (this.drawType) {
                    return this.drawType;
                }
                if (this.geometryType.length === 1) {
                    return this.geometryType;
                }
                if (this.geometryTypeChoice) {
                    return this.geometryTypeChoice;
                }
                // takes the last char in PLV
                return this.geometryType.charAt(this.geometryType.length - 1);
            },
            // returns the type in the format known to openLayers (Point, LineString, Polygon)
            currentDrawType() {
                const allCodes = Object.entries(FEEDBACKS_GEOMETRY_TYPE_CODES);
                const allTypes = Object.entries(FEEDBACKS_GEOMETRY_TYPES);
                const currentCodeDef = allCodes.find(
                    ([key, value]) => value === this.currentDrawTypeCode
                );
                const currentGeometryObject = allTypes.find(
                    ([key]) => key === currentCodeDef[0]
                );
                return currentGeometryObject[1];
            },
            isWebFeedback() {
                return this.$store.getters.getWebThemeHasFeedbackType;
            },

            // Always show snap button in mobile, in web it depends on showSnap setting
            $_showSnapButton() {
                return this.isWebFeedback ? this.getWebTheme?.data?.showSnap : true;
            }
        },

        mounted() {
            // If the snap button is visible in web, it should be active immediately
            if (this.getWebTheme?.data?.showSnap) {
                this.useSnap = true;
            }

            this.drawType = this.currentDrawTypeCode;
        },

        destroyed() {
            this.finishDrawing();
        },

        methods: {
            ...mapActions(['getLayerSelection']),
            ...mapMutations(['SET_DRAW_GEOMETRYTYPE_DEFAULT']),
            async startDrawing(useExistingFeature = true) {
                this.drawObject.geometryType = this.currentDrawType;
                this.draw = new DrawOnMap(this.map);
                this.setSnappingPoints();
                this.draw.start(this.currentDrawType, this.drawObject);
                const drawing = this.draw.draw(
                    this.isUsingPoint && !this.loadCoordinates
                );
                if (useExistingFeature && this.loadCoordinates) {
                    this.$store.commit('RESET_MAP_FEATURES', { mapName: 'main' });
                    this.$store.commit('MAP_ADD_DRAWING', {
                        mapName: 'main',
                        feature: null
                    });
                    this.draw.appendCoordinates(this.loadCoordinates);
                    this.draw.finishDrawing();
                    this.draw.onDrawEnd();
                }
                await drawing;
                this.finishDrawing();
            },

            setSnappingPoints: _.debounce(async function () {
                if (!this.draw) {
                    return;
                }
                if (!this.useSnap) {
                    this.draw.setSnap([]);
                    return;
                }

                const bbox = this.map.getView().calculateExtent(this.map.getSize());
                const featuresResult = await this.getLayerSelection({
                    bbox,
                    force: true,
                    checkLayerAttributes: false
                });
                const featureCoordinates = featuresResult
                    .flatMap(({ features }) => features)
                    .map((feature) => {
                        const geometry = feature.getGeometry();
                        const geometryType = geometry.getType();
                        if (geometryType === FEEDBACKS_GEOMETRY_TYPES.POLYGON) {
                            return geometry.getCoordinates()[0];
                        } else if (
                            geometryType === FEEDBACKS_GEOMETRY_TYPES.POINT
                        ) {
                            return [geometry.getCoordinates()];
                        } else {
                            return geometry.getCoordinates();
                        }
                    })
                    .flatMap((point) => point);

                this.draw.setSnap(featureCoordinates);
            }, 100),

            finishDrawing() {
                this.stopDrawing();
                this.$emit('finish', this.drawObject);
            },

            resetDrawing(clear) {
                this.stopDrawing();
                if (clear) {
                    this.drawObject = { ...drawObject };
                } else if (this.drawType === FEEDBACKS_GEOMETRY_TYPE_CODES.POINT) {
                    // allways reset length, area, points and polygon for points
                    this.drawObject.length = null;
                    this.drawObject.area = null;
                    this.drawObject.polygon = null;
                    this.drawObject.points = 0;
                }
                this.startDrawing(!clear);
            },

            stopDrawing() {
                if (!this.draw) {
                    return;
                }
                this.draw.finishDrawing();
                this.draw.end();
            },

            useLocationAsPoint() {
                const gpsLocation = this.$_getGpsPosition();
                if (!gpsLocation) {
                    return;
                }
                this.draw.appendCoordinates([gpsLocation]);
            },

            $_getGpsPosition() {
                const gpsTracker = this.$store.getters.getMainMap.gpsTracker;
                return gpsTracker?.getPosition();
            }
        },

        watch: {
            'drawObject.coordinates': function (newVal, oldVal) {
                if (newVal) {
                    if (!oldVal || newVal.length >= oldVal.length) {
                        this.drawObject.points = newVal.length - 1;
                    }
                }
            },
            'drawObject.polygon': function (newVal) {
                if (newVal) {
                    const area = OlGetArea(newVal);
                    const length = OlGetLength(newVal);
                    this.drawObject.area = Math.round(area * 100) / 100;
                    this.drawObject.length = Math.round(length * 100) / 100;
                }
            },
            'drawObject.points': function (newVal) {
                if (newVal < 2 && this.isUsingLine) {
                    this.useDelete = false;
                }
                if (newVal < 4 && this.isUsingPolygon) {
                    this.useDelete = false;
                }
            },
            drawObject: {
                handler: function (newVal) {
                    this.$emit('changed', newVal);
                },
                deep: true
            },
            viewport: function () {
                this.setSnappingPoints();
            },
            useSnap: function () {
                this.setSnappingPoints();
            },
            useDelete: function (newVal) {
                this.draw.setDeleteMode(newVal);
            },
            drawType: function (newVal, oldVal) {
                if (this.$store.getters.isMobile) {
                    // update both, don't have info about feedback or wfs-t
                    this.SET_DRAW_GEOMETRYTYPE_DEFAULT({
                        panel: INTERFACE_PANEL_TRIGGERS.FEEDBACK_EDIT,
                        geometryType: newVal
                    });
                    this.SET_DRAW_GEOMETRYTYPE_DEFAULT({
                        panel: INTERFACE_PANEL_TRIGGERS.WFS_T_EDIT,
                        geometryType: newVal
                    });
                }
                this.drawObject.geometryType = this.currentDrawType;
                // stop the current drawing and start the new with the new type
                this.resetDrawing();
                // signal the new drawing type so the correct neasurements can be shown for the type
                this.$emit('changedType', newVal);
                // use existing drawing for new drawingtype if possible
                if (this.drawObject.coordinates) {
                    if (
                        oldVal === FEEDBACKS_GEOMETRY_TYPE_CODES.POLYGON &&
                        newVal === FEEDBACKS_GEOMETRY_TYPE_CODES.LINE
                    ) {
                        this.draw.appendCoordinates(this.drawObject.coordinates);
                        // remove the last point so the line is not closed coming from polygon
                        this.draw.removeLastpoint();
                    }

                    // Copy line coordinates when there are more than 1.
                    if (
                        oldVal === FEEDBACKS_GEOMETRY_TYPE_CODES.LINE &&
                        newVal === FEEDBACKS_GEOMETRY_TYPE_CODES.POLYGON &&
                        this.drawObject.coordinates.length > 1
                    ) {
                        this.draw.appendCoordinates(this.drawObject.coordinates);
                    }

                    // If we go from line- or polygon drawing to point drawing we must reset
                    if (
                        (oldVal === FEEDBACKS_GEOMETRY_TYPE_CODES.LINE ||
                            oldVal === FEEDBACKS_GEOMETRY_TYPE_CODES.POLYGON) &&
                        newVal === FEEDBACKS_GEOMETRY_TYPE_CODES.POINT
                    ) {
                        this.resetDrawing(true);
                    }
                }
            }
        }
    };
</script>

<style scoped lang="scss">
.toolbar {
    position: fixed;
    top: var(--paneltop);
    left: 50%;
    transform: translateX(-50%);

    & > * {
        background-color: white;
    }
}
button img {
    width: 100%;
}
button.active {
    background-color: $blue-05;
    color: $gray-00;
}
</style>
